// src/pages/Admin/Business/Finance/components/CommitmentsTimeline.jsx

import React, { useMemo } from "react";
import {
  addDays,
  fmtMoney,
  formatDate,
  getCommitmentMeta,
  parseISODate,
  startOfDay,
} from "../financeCommitments.utils";

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function daysBetween(a, b) {
  return Math.round((startOfDay(b) - startOfDay(a)) / (1000 * 60 * 60 * 24));
}

function toneToClasses(tone) {
  switch (tone) {
    case "red":
      return "bg-rose-500/90";
    case "orange":
      return "bg-orange-500/90";
    case "yellow":
      return "bg-amber-400/90";
    case "green":
      return "bg-emerald-500/90";
    case "blue":
      return "bg-blue-500/90";
    default:
      return "bg-slate-400/90";
  }
}

const CommitmentsTimeline = ({ items = [], daysWindow = 120 }) => {
  const today = startOfDay(new Date());
  const windowEnd = addDays(today, daysWindow);

  const visibleItems = useMemo(() => {
    return items
      .map((item) => {
        const start = parseISODate(item.startDate || item.dueDate);
        const end = parseISODate(item.endDate || item.dueDate || item.startDate);

        if (!start && !end) return null;

        const barStart = start || end;
        const barEnd = end || start;

        if (!barStart || !barEnd) return null;
        if (barEnd < addDays(today, -15)) return null;
        if (barStart > windowEnd) return null;

        return {
          ...item,
          __barStart: barStart,
          __barEnd: barEnd,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.__barStart - b.__barStart)
      .slice(0, 10);
  }, [items, today, windowEnd]);

  if (visibleItems.length === 0) {
    return (
      <div className="grid h-[280px] place-items-center text-sm text-slate-400 dark:text-slate-500">
        Aucun élément visible sur la période choisie.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <div className="min-w-[760px]">
          <div className="ml-[250px] grid grid-cols-4 gap-0 text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            {Array.from({ length: 4 }).map((_, i) => {
              const markerDate = addDays(today, i * (daysWindow / 4));
              return (
                <div
                  key={i}
                  className="border-l border-dashed border-slate-200 px-3 py-2 dark:border-slate-700"
                >
                  {formatDate(markerDate)}
                </div>
              );
            })}
          </div>

          <div className="space-y-3">
            {visibleItems.map((item) => {
              const meta = getCommitmentMeta(item);

              const startOffset = daysBetween(today, item.__barStart);
              const endOffset = daysBetween(today, item.__barEnd);

              const left = clamp((startOffset / daysWindow) * 100, 0, 100);
              const right = clamp((endOffset / daysWindow) * 100, 0, 100);
              const width = Math.max(2, right - left);

              return (
                <div
                  key={item.uid}
                  className="grid grid-cols-[240px_minmax(520px,1fr)] items-center gap-3"
                >
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/50">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="truncate font-semibold text-slate-900 dark:text-slate-50">
                        {item.title}
                      </div>
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold text-white ${
                          item.entityType === "depense" ? "bg-blue-500" : "bg-purple-500"
                        }`}
                      >
                        {item.entityType === "depense" ? "Dépense" : "Échéance"}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {item.associationName} • {fmtMoney(item.displayAmount, true)}
                    </div>
                  </div>

                  <div className="relative h-14 rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
                    <div className="absolute inset-y-0 left-1/4 w-px bg-slate-200 dark:bg-slate-700" />
                    <div className="absolute inset-y-0 left-2/4 w-px bg-slate-200 dark:bg-slate-700" />
                    <div className="absolute inset-y-0 left-3/4 w-px bg-slate-200 dark:bg-slate-700" />

                    <div
                      className={`absolute top-1/2 h-6 -translate-y-1/2 rounded-full ${toneToClasses(
                        meta.tone
                      )}`}
                      style={{
                        left: `${left}%`,
                        width: `${width}%`,
                      }}
                      title={`${item.title} • ${formatDate(item.__barStart)} → ${formatDate(
                        item.__barEnd
                      )}`}
                    />

                    <div className="absolute inset-0 flex items-center justify-between px-4 text-xs font-medium text-slate-600 dark:text-slate-300">
                      <span>{formatDate(item.__barStart)}</span>
                      <span>{formatDate(item.__barEnd)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="text-xs text-slate-500 dark:text-slate-400">
        Les dépenses ponctuelles apparaissent comme des barres courtes. Les échéances
        montrent leur période entre début et fin.
      </div>
    </div>
  );
};

export default CommitmentsTimeline;